import { localize } from '../i18n';
import { getDeviceUuid, getEntitlements, recordAdEvent, getRewardVerificationPolicy } from '../api';

const APP_RUNTIME_INFO_KEY = 'pill_reminder_app_runtime_info';
const ENTITLEMENT_CACHE_KEY = 'pill_reminder_entitlements';
const ENTITLEMENT_CACHE_TTL_MS = 5 * 60 * 1000;
let entitlementRevision = 0;

export function rewardedAdFailureMessage(reason) {
    if (reason === 'update_required') return localize('보상 광고를 이용하려면 앱을 업데이트해주세요.');
    if (reason === 'verification_pending') return localize('광고 시청 보상을 확인 중이에요. 잠시 후 다시 눌러주세요. 확인되면 광고를 다시 보지 않고 받을 수 있어요.');
    if (reason === 'verification_unavailable') return localize('보상 확인 서버에 연결하지 못했어요. 잠시 후 다시 시도해주세요.');
    return localize('광고가 아직 준비되지 않았어요. 잠시 후 다시 시도해주세요.');
}

function getRuntimeInfo() {
    try {
        return JSON.parse(localStorage.getItem(APP_RUNTIME_INFO_KEY) || 'null') || {};
    } catch {
        return {};
    }
}

function getCachedEntitlements() {
    try {
        const cached = JSON.parse(localStorage.getItem(ENTITLEMENT_CACHE_KEY) || 'null');
        if (!cached?.receivedAt || cached.deviceUuid !== getDeviceUuid()) return null;
        if (Date.now() - new Date(cached.receivedAt).getTime() > ENTITLEMENT_CACHE_TTL_MS) return null;
        return cached;
    } catch {
        return null;
    }
}

function saveEntitlementCache(data) {
    try {
        localStorage.setItem(ENTITLEMENT_CACHE_KEY, JSON.stringify({
            ...data,
            deviceUuid: getDeviceUuid(),
            receivedAt: new Date().toISOString(),
        }));
    } catch {}
}

export function clearCachedEntitlements() {
    entitlementRevision += 1;
    try {
        localStorage.removeItem(ENTITLEMENT_CACHE_KEY);
    } catch {}
}

export function applyPurchasedEntitlements(data) {
    clearCachedEntitlements();
    if (data?.adFree === true) saveEntitlementCache(data);
}

export function getEntitlementRevision() { return entitlementRevision; }

// Native events can arrive after logout/deletion. Re-read the current server context
// instead of persisting the event's old account entitlement.
export async function reconcilePurchasedEntitlements(message) {
    const uuid = getDeviceUuid();
    if (message.deviceUuid && message.deviceUuid !== uuid) return null;
    clearCachedEntitlements();
    const revision = entitlementRevision;
    try {
        const current = await getEntitlements(uuid);
        if (revision !== entitlementRevision || uuid !== getDeviceUuid()) return null;
        if (typeof current?.adFree !== 'boolean') return null;
        saveEntitlementCache(current);
        return current;
    } catch { return null; }
}

export function hasCachedAdFreeEntitlement() {
    return getCachedEntitlements()?.adFree === true;
}

function dispatchRewardComplete(feature, reason = null) {
    window.postMessage(JSON.stringify({ type: 'REWARDED_AD_COMPLETE', feature, reason }), '*');
}

function dispatchRewardUnavailable(feature, reason = null) {
    window.postMessage(JSON.stringify({ type: 'REWARDED_AD_UNAVAILABLE', feature, reason }), '*');
}

function dispatchInterstitialResult(type, feature, reason = null) {
    window.postMessage(JSON.stringify({ type, feature, reason }), '*');
}

function recordAd(uuid, payload) {
    try {
        return recordAdEvent(uuid, payload).catch(() => {});
    } catch {
        return Promise.resolve();
    }
}

export function requestAd(adType, feature) {
    if (!window.ReactNativeWebView) return false;

    const runtimeInfo = getRuntimeInfo();
    const uuid = getDeviceUuid();
    const requestRevision = entitlementRevision;
    const basePayload = {
        adType,
        feature,
        platform: runtimeInfo.platform || 'webview',
        appVersion: runtimeInfo.appVersion || null,
        runtimeVersion: runtimeInfo.runtimeVersion || null,
        channel: runtimeInfo.channel || null,
    };

    const sendNativeAdRequest = async () => {
        if (adType === 'rewarded' && runtimeInfo.capabilities?.verifiedRewardedAds !== true) {
            let policy;
            try { policy = await getRewardVerificationPolicy(uuid); } catch {}
            if (entitlementRevision !== requestRevision || getDeviceUuid() !== uuid) return;
            if (policy?.enabled !== false) {
                dispatchRewardUnavailable(feature, policy?.enabled === true ? 'update_required' : 'verification_unavailable');
                return;
            }
        }
        if (entitlementRevision !== requestRevision || getDeviceUuid() !== uuid) return;
        recordAd(uuid, {
            ...basePayload,
            adType,
            eventType: 'request',
            feature,
            errorMessage: 'source=web',
        });

        window.ReactNativeWebView.postMessage(JSON.stringify({
            type: adType === 'rewarded' ? 'SHOW_REWARDED_AD' : 'SHOW_INTERSTITIAL',
            feature,
        }));
    };

    const handleAdFree = () => {
        recordAd(uuid, {
            ...basePayload,
            eventType: 'skipped_ad_free',
            errorMessage: 'source=web',
        }).finally(() => {
            if (adType === 'rewarded') {
                dispatchRewardComplete(feature, 'ad_free');
            } else {
                dispatchInterstitialResult('INTERSTITIAL_AD_SKIPPED_AD_FREE', feature, 'ad_free');
            }
        });
    };

    const cached = getCachedEntitlements();
    if (cached?.adFree) {
        handleAdFree();
        return true;
    }

    getEntitlements(uuid)
        .then((entitlements) => {
            if (requestRevision !== entitlementRevision || uuid !== getDeviceUuid()) {
                if (uuid === getDeviceUuid() && hasCachedAdFreeEntitlement()) handleAdFree();
                else if (adType === 'rewarded') dispatchRewardUnavailable(feature, 'entitlement_changed');
                else dispatchInterstitialResult('INTERSTITIAL_AD_UNAVAILABLE', feature, 'entitlement_changed');
                return;
            }
            if (typeof entitlements?.adFree !== 'boolean') throw new Error('Invalid entitlement response');
            saveEntitlementCache(entitlements);
            if (entitlements.adFree) {
                handleAdFree();
                return;
            }
            sendNativeAdRequest();
        })
        .catch(() => {
            recordAd(uuid, {
                ...basePayload,
                eventType: 'skipped_entitlement_check_failed',
                errorMessage: 'source=web',
            });
            if (adType === 'rewarded') dispatchRewardUnavailable(feature, 'entitlement_check_failed');
            else dispatchInterstitialResult('INTERSTITIAL_AD_UNAVAILABLE', feature, 'entitlement_check_failed');
        });
    return true;
}
